import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Agent } from "@/api/generated";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { extractHttpStatus } from "@/utilities/extractHttpStatus";

type AgentDetailsState = { id: number } & (
  | { status: "loading" | "error" | "notFound" }
  | { status: "ready"; agent: Agent }
);

interface UseGetAgentDetails {
  fetchAgentById: () => Promise<void>;
  agent: Agent | null;
  loading: boolean;
  error: boolean;
  // API が 404 を返した（提携終了・退会したプランナー、存在しない ID）。
  notFound: boolean;
}

export const useGetAgentDetails = (id: number): UseGetAgentDetails => {
  const [state, setState] = useState<AgentDetailsState>({ id, status: "loading" });
  const requestVersion = useRef(0);
  // IDが A → B → A と戻っても、最初のAの呼び出しは別の取得世代として扱う。
  const scope = useMemo(() => ({ id }), [id]);
  const activeScope = useRef<typeof scope | null>(null);
  const fp = useMemo(() => PropallyApiFactory(), []);

  const fetchAgentById = useCallback(async () => {
    // 古いクロージャは、現在のリクエストを無効化する前に止める。
    if (activeScope.current !== scope) return;
    const version = ++requestVersion.current;
    setState({ id, status: "loading" });
    try {
      const data = await fp.v1AgentsAgentIdGet(id, { withCredentials: true });
      if (version !== requestVersion.current) return;
      if (data.status !== 200 || !data.data) throw new Error("Agent detail response is empty");
      setState({ id, status: "ready", agent: data.data });
    } catch (e) {
      if (version !== requestVersion.current) return;
      // 失敗は完了した状態として扱い、再試行を可能にする。前のプロフィールは残さない。
      if (extractHttpStatus(e) === 404) {
        setState({ id, status: "notFound" });
      } else {
        setState({ id, status: "error" });
        // HTTPエラーの報告はCustomApiFactoryに集約する（console.errorによる二重送信を避ける）。
      }
    }
  }, [fp, id, scope]);

  useEffect(() => {
    activeScope.current = scope;
    void fetchAgentById();
    // ID切替・離脱後の応答や、再試行より古い応答で表示を上書きしない。
    return () => {
      activeScope.current = null;
      requestVersion.current += 1;
    };
  }, [fetchAgentById, scope]);

  // 新しいIDのeffectが動く前の描画でも、前のプランナーを表示しない。
  const current = Object.is(state.id, id) ? state : { status: "loading" as const };
  return {
    fetchAgentById,
    agent: current.status === "ready" ? current.agent : null,
    loading: current.status === "loading",
    error: current.status === "error",
    notFound: current.status === "notFound",
  };
};
